import { useDebounce } from "../../hooks/useDebounce";
import { FC, useRef, useEffect } from "react";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { CircularProgress } from "@mui/material";
import { SELECT_ALL } from "../../config";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface IProps {
  placeholder: string;
  isDisabled?: boolean;
  positionsList: any[];
  value: any[];
  handleChange: (event: any, newValue: readonly any[]) => void;
  onSearch?: (query: string) => void;
  onScrollEnd?: () => void;
  isLoading?: boolean;
  withSelectAll?: boolean;
}

const SELECT_ALL_ITEM = { id: "__SELECT_ALL__", name: SELECT_ALL };

export const AutocompleteCheckBox: FC<IProps> = ({
  positionsList,
  isDisabled,
  value,
  handleChange,
  placeholder,
  onSearch,
  onScrollEnd,
  isLoading,
  withSelectAll,
}) => {
  const listboxRef = useRef<HTMLUListElement | null>(null);
  const lastScrollTop = useRef(0);
  const { debouncedWrapper } = useDebounce(onSearch);

  useEffect(() => {
    if (listboxRef.current) {
      listboxRef.current.scrollTop = lastScrollTop.current;
    }
  }, [positionsList, value]);

  const options = withSelectAll
    ? [SELECT_ALL_ITEM, ...positionsList]
    : positionsList;

  const onChangeHandler = (event: any, newValue: readonly any[]) => {
    if (listboxRef.current) {
      lastScrollTop.current = listboxRef.current.scrollTop;
    }

    if (!withSelectAll) {
      handleChange(event, newValue);
      return;
    }

    const isSelectAllClicked = newValue.some(
      (item) => item.id === SELECT_ALL_ITEM.id
    );

    if (isSelectAllClicked) {
      const allSelected = value.length === positionsList.length;
      handleChange(event, allSelected ? [] : positionsList);
    } else {
      handleChange(event, newValue);
    }
  };

  return (
    <Autocomplete
      multiple
      id="checkboxes-tags-demo"
      options={options}
      disableCloseOnSelect
      value={value}
      onChange={onChangeHandler}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onInputChange={(event, newInputValue) => {
        debouncedWrapper?.(newInputValue);
      }}
      ListboxProps={{
        ref: (ref: HTMLUListElement) => {
          listboxRef.current = ref;
          if (ref) {
            ref.onscroll = () => {
              if (ref.scrollTop + ref.clientHeight >= ref.scrollHeight - 100) {
                onScrollEnd?.();
              }
              lastScrollTop.current = ref.scrollTop;
            };
          }
        },
      }}
      renderOption={(props, option, { selected }) => {
        if (option.id === SELECT_ALL_ITEM.id) {
          const allSelected = value.length === positionsList.length;
          const indeterminate = value.length > 0 && !allSelected;

          return (
            <li {...props} key={option.id}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={allSelected}
                indeterminate={indeterminate}
                size="small"
              />
              {option.name}
            </li>
          );
        }

        return (
          <li {...props} key={option.id}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {option.name}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? (
                  <CircularProgress color="primary" size={25} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      disabled={isDisabled}
    />
  );
};
